/**
 * Admin Analytics console — a first-party "Google Analytics".
 *
 *   - LIVE strip: who is on the site right now, what page, for how long
 *   - Overview: KPIs, traffic timeline, top pages, device/browser/OS/geo/
 *     referrer/landing breakdowns
 *   - Visitors: paginated per-visitor table → full drill-down modal with every
 *     session and page-by-page timeline
 *
 * Data comes from /api/admin/analytics/* (live | overview | visitors |
 * visitor/:id). Live + overview auto-refresh while the tab is visible.
 */
import { useEffect, useState } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { formatDate } from '../../utils/helpers';
import UsLiveMap from './UsLiveMap';
import Icon from './Icon';

// ─── formatting helpers ──────────────────────────────────────────────────────
const dur = (ms) => {
  if (!ms || ms < 1000) return `${ms ? Math.round(ms / 1000) : 0}s`;
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h) return `${h}h ${m % 60}m`;
  if (m) return `${m}m ${s % 60}s`;
  return `${s}s`;
};
const secs = (s) => dur((s || 0) * 1000);
const num = (n) => (n == null ? '0' : Number(n).toLocaleString());

// Device labels are shown as plain text alongside the device name; no pictographs.
const DEVICE_ICON = { desktop: '', mobile: '', tablet: '', bot: '' };
const RANGES = [
  { id: 'today', label: 'Today' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All time' },
];

// Short page label from a path (keeps it readable in tables).
const shortPath = (p) => {
  if (!p) return '/';
  const clean = p.split('?')[0];
  return clean.length > 42 ? clean.slice(0, 41) + '…' : clean;
};

// ─── tiny presentational atoms ───────────────────────────────────────────────
function Card({ label, value, sub, accent }) {
  return (
    <div className={'rounded-xl border p-4 shadow-sm ' + (accent ? 'bg-tea-50 border-tea-200' : 'bg-white')}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={'text-2xl font-bold tabular-nums ' + (accent ? 'text-tea-700' : 'text-gray-900')}>{value}</div>
      {sub != null && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

function BarList({ title, items, render, accent = 'bg-tea-500', emptyLabel = 'No data yet' }) {
  const max = Math.max(1, ...items.map(i => i.value));
  return (
    <div className="bg-white rounded-xl border p-5 shadow-sm">
      <h3 className="text-sm font-bold text-gray-700 mb-3">{title}</h3>
      {items.length === 0 ? (
        <div className="text-sm text-gray-400 py-4 text-center">{emptyLabel}</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="relative">
              <div className="flex items-center justify-between text-sm relative z-10 px-2 py-1">
                <span className="truncate text-gray-700" title={it.label}>{render ? render(it) : it.label}</span>
                <span className="tabular-nums font-medium text-gray-600 ml-3 flex-shrink-0">{num(it.value)}{it.suffix || ''}</span>
              </div>
              <div className={'absolute inset-y-0 left-0 rounded ' + accent + ' opacity-15'}
                   style={{ width: `${(it.value / max) * 100}%` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pageviews-over-time bars.
function Timeline({ series }) {
  if (!series || series.length === 0) {
    return <div className="text-sm text-gray-400 py-8 text-center">No traffic in this range yet</div>;
  }
  const max = Math.max(1, ...series.map(s => Number(s.pageviews)));
  const showEvery = Math.ceil(series.length / 12);
  return (
    <div>
      <div className="flex items-end gap-1 h-40">
        {series.map((s, i) => {
          const h = Math.max(2, (Number(s.pageviews) / max) * 100);
          return (
            <div key={i} className="flex-1 flex flex-col justify-end group relative" title={`${s.bucket} — ${s.pageviews} views, ${s.visitors} visitors`}>
              <div className="w-full bg-tea-500 rounded-t hover:bg-tea-600 transition-colors" style={{ height: `${h}%` }} />
              <div className="absolute -top-7 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 text-white text-[10px] rounded px-1.5 py-0.5 whitespace-nowrap z-20">
                {num(s.pageviews)} views
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 mt-1">
        {series.map((s, i) => (
          <div key={i} className="flex-1 text-[9px] text-gray-400 text-center truncate">
            {i % showEvery === 0 ? s.bucket.replace(/^\d{4}-/, '').replace(' ', ' ') : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  LIVE strip
// ═══════════════════════════════════════════════════════════════════════════
function LivePanel() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = () => api.get('/admin/analytics/live')
      .then(r => { if (alive) setData(r.data); })
      .catch(() => {});
    load();
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const count = data?.count || 0;

  return (
    <div className="bg-white rounded-xl border shadow-sm mb-6 overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50">
        <div className="flex items-center gap-3">
          <span className="relative flex h-3 w-3">
            <span className={'absolute inline-flex h-full w-full rounded-full opacity-75 ' + (count ? 'bg-blue-400 animate-ping' : 'bg-gray-300')} />
            <span className={'relative inline-flex rounded-full h-3 w-3 ' + (count ? 'bg-blue-500' : 'bg-gray-400')} />
          </span>
          <span className="text-lg font-bold text-gray-900">{count}</span>
          <span className="text-sm text-gray-500">{count === 1 ? 'visitor' : 'visitors'} on the site right now</span>
        </div>
        <span className="text-gray-400 text-sm">{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="border-t">
          {count === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Nobody is browsing right now.</div>
          ) : (
            <div>
              {/* Where live visitors are on the site right now (live page breakdown) */}
              <div className="p-4 bg-gray-50/60 border-b">
                <BarList
                  title="Where they are right now"
                  accent="bg-blue-500"
                  items={(data.byPage || []).map(p => ({
                    label: shortPath(p.key),
                    value: p.count,
                    suffix: ' here',
                  }))}
                  emptyLabel="No active pages right now"
                />
              </div>
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Who</th>
                    <th className="text-left px-4 py-2 font-medium">Current page</th>
                    <th className="text-left px-4 py-2 font-medium">Device</th>
                    <th className="text-left px-4 py-2 font-medium">Location</th>
                    <th className="text-right px-4 py-2 font-medium">On page</th>
                    <th className="text-right px-4 py-2 font-medium">Session</th>
                    <th className="text-right px-4 py-2 font-medium">Views</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.sessions.map(s => (
                    <tr key={s.sessionId} className="hover:bg-gray-50">
                      <td className="px-4 py-2.5">
                        {s.name ? (
                          <span className="font-medium text-gray-800">{s.name}</span>
                        ) : (
                          <span className="text-gray-400">Anonymous</span>
                        )}
                        {s.email && <div className="text-xs text-gray-400">{s.email}</div>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-700" title={s.path}>{shortPath(s.path)}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {DEVICE_ICON[s.device] || ''} {s.browser} · {s.os}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">
                        {s.region ? `${s.region}, ` : ''}{s.country || '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{secs(s.onPageSeconds)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{secs(s.sessionSeconds)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{s.pageViews}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  OVERVIEW
// ═══════════════════════════════════════════════════════════════════════════
function Overview({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const load = () => api.get('/admin/analytics/overview', { params: { range } })
      .then(r => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setLoading(false); toast.error('Failed to load analytics'); } });
    load();
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [range]);

  if (loading && !data) return <div className="py-12 text-center text-gray-400">Loading analytics…</div>;
  if (!data) return null;

  const k = data.kpis;
  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card label="Pageviews" value={num(k.pageviews)} accent />
        <Card label="Unique visitors" value={num(k.visitors)} sub={`${num(k.newVisitors)} new · ${num(k.returningVisitors)} returning`} />
        <Card label="Sessions" value={num(k.sessions)} sub={`${k.pageviewsPerSession} pages/session`} />
        <Card label="Avg. time on page" value={dur(k.avgTimeOnPageMs)} />
        <Card label="Avg. session" value={dur(k.avgSessionMs)} />
        <Card label="Bounce rate" value={`${k.bounceRate}%`} sub={`${num(k.loggedInVisitors)} logged-in`} />
      </div>

      {/* Traffic timeline */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-bold text-gray-700 mb-4">Traffic over time</h3>
        <Timeline series={data.timeseries} />
      </div>

      {/* Live US map */}
      <UsLiveMap usStates={data.usStates || []} />

      {/* Top pages */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <h3 className="text-sm font-bold text-gray-700 px-5 pt-5 pb-3">Top pages — time spent &amp; engagement</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-2 font-medium">Page</th>
                <th className="text-right px-4 py-2 font-medium">Views</th>
                <th className="text-right px-4 py-2 font-medium">Visitors</th>
                <th className="text-right px-4 py-2 font-medium">Avg. time</th>
                <th className="text-right px-5 py-2 font-medium">Avg. scroll</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.topPages.map((p, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-5 py-2.5 text-gray-700" title={p.path}>{shortPath(p.path)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{num(p.views)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{num(p.visitors)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{dur(Math.round(p.avg_time_ms))}</td>
                  <td className="px-5 py-2.5 text-right tabular-nums">{Math.round(p.avg_scroll)}%</td>
                </tr>
              ))}
              {data.topPages.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">No pageviews in this range yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign performance — which marketing effort is working best */}
      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <h3 className="text-sm font-bold text-gray-700 px-5 pt-5 pb-1">Campaign performance</h3>
        <p className="text-xs text-gray-400 px-5 pb-3">Tag a homepage link with <code className="bg-gray-100 rounded px-1">?c=mahalo</code> to attribute its traffic here.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-2 font-medium">Campaign</th>
                <th className="text-right px-4 py-2 font-medium">Sessions</th>
                <th className="text-right px-4 py-2 font-medium">Visitors</th>
                <th className="text-right px-4 py-2 font-medium">Avg. session</th>
                <th className="text-right px-5 py-2 font-medium">Bounce</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(data.campaigns || []).map((c, i) => {
                const entries = Number(c.entries) || 0;
                const bounce = entries ? Math.round((Number(c.bounced) || 0) / entries * 100) : 0;
                return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-5 py-2.5 text-gray-700 font-medium">{c.label}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{num(c.entries)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{num(c.visitors)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{dur(Math.round(c.avg_session_ms))}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{bounce}%</td>
                  </tr>
                );
              })}
              {(!data.campaigns || data.campaigns.length === 0) && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-gray-400">No campaign traffic in this range yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Breakdowns */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <BarList title="Devices" accent="bg-blue-500"
          items={data.devices.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Browsers" accent="bg-indigo-500"
          items={data.browsers.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Operating systems" accent="bg-purple-500"
          items={data.oses.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Countries / regions" accent="bg-blue-500"
          items={data.countries.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Languages" accent="bg-amber-500"
          items={data.languages.map(d => ({ label: d.label, value: Number(d.views) }))} />
        <BarList title="Traffic sources (referrers)" accent="bg-rose-500"
          items={data.referrers.map(d => ({ label: d.label, value: Number(d.sessions), suffix: ' sess' }))} />
        <BarList title="Landing pages" accent="bg-blue-500"
          items={data.landingPages.map(d => ({ label: shortPath(d.path), value: Number(d.entries) }))} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  VISITORS table + drill-down
// ═══════════════════════════════════════════════════════════════════════════
function VisitorModal({ visitorId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/admin/analytics/visitor/${visitorId}`)
      .then(r => setData(r.data))
      .catch(() => { toast.error('Failed to load visitor'); onClose(); });
  }, [visitorId, onClose]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full my-8" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">
                {data?.user ? data.user.name : 'Anonymous visitor'}
              </h2>
              {data?.live && (
                <span className="flex items-center gap-1 text-xs font-medium text-blue-600">
                  <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" /> live now
                </span>
              )}
            </div>
            <div className="text-xs text-gray-400 font-mono">{visitorId}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        {!data ? (
          <div className="py-16 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Identity */}
            {data.user && (
              <div className="bg-tea-50 border border-tea-200 rounded-xl p-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-gray-500">Email:</span> {data.user.email}</div>
                  <div><span className="text-gray-500">Role:</span> {data.user.role}</div>
                  <div><span className="text-gray-500">Member since:</span> {formatDate(data.user.joined)}</div>
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card label="Pageviews" value={num(data.summary.pageviews)} />
              <Card label="Sessions" value={num(data.summary.sessions)} />
              <Card label="Total time" value={dur(data.summary.totalDurationMs)} />
              <Card label="Avg / page" value={dur(data.summary.avgTimeOnPageMs)} />
            </div>

            {/* Environment */}
            <div className="bg-gray-50 rounded-xl p-4 text-sm grid grid-cols-2 md:grid-cols-3 gap-2">
              <div><span className="text-gray-500">Device:</span> {DEVICE_ICON[data.latest.device] || ''} {data.latest.device}</div>
              <div><span className="text-gray-500">Browser:</span> {data.latest.browser}</div>
              <div><span className="text-gray-500">OS:</span> {data.latest.os}</div>
              <div><span className="text-gray-500">Location:</span> {data.latest.region ? data.latest.region + ', ' : ''}{data.latest.country || '—'}</div>
              <div><span className="text-gray-500">Timezone:</span> {data.latest.timezone || '—'}</div>
              <div><span className="text-gray-500">Language:</span> {data.latest.language || '—'}</div>
              <div><span className="text-gray-500">Screen:</span> {data.latest.screen || '—'}</div>
              <div><span className="text-gray-500">Viewport:</span> {data.latest.viewport || '—'}</div>
              <div><span className="text-gray-500">IP:</span> <span className="font-mono text-xs">{data.latest.ip || '—'}</span></div>
              {(data.latest.utm?.source || data.latest.utm?.campaign) && (
                <div className="col-span-2 md:col-span-3"><span className="text-gray-500">Campaign:</span> {data.latest.utm.source} / {data.latest.utm.medium} / {data.latest.utm.campaign}</div>
              )}
              <div className="col-span-2 md:col-span-3 text-xs text-gray-400">
                First seen {formatDate(data.summary.firstSeen)} · Last seen {formatDate(data.summary.lastSeen)}
              </div>
            </div>

            {/* Sessions */}
            <div>
              <h3 className="text-sm font-bold text-gray-700 mb-2">Sessions &amp; page journeys</h3>
              <div className="space-y-3">
                {data.sessions.map((s, i) => (
                  <div key={s.sessionId} className="border rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-2 flex items-center justify-between text-xs text-gray-600">
                      <span>{formatDate(s.startedAt)} · {DEVICE_ICON[s.device] || ''} {s.browser}/{s.os}{s.country ? ' · ' + s.country : ''}</span>
                      <span className="tabular-nums">{s.pageViews} pages · {dur(s.durationMs)}</span>
                    </div>
                    <ol className="divide-y">
                      {s.pages.map((pg, j) => (
                        <li key={j} className="px-4 py-2 flex items-center justify-between text-sm">
                          <div className="min-w-0">
                            <span className="text-gray-700" title={pg.path}>{shortPath(pg.path)}</span>
                            {pg.title && <span className="text-gray-400 text-xs ml-2 truncate">{pg.title}</span>}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0 text-xs text-gray-500 tabular-nums">
                            <span>{dur(pg.durationMs)}</span>
                            <span>{pg.scrollPct}% scroll</span>
                            <span>{pg.clicks} clicks</span>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Visitors({ range }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  useEffect(() => { setPage(1); }, [range]);
  useEffect(() => {
    let alive = true;
    api.get('/admin/analytics/visitors', { params: { range, page } })
      .then(r => { if (alive) setData(r.data); })
      .catch(() => toast.error('Failed to load visitors'));
    return () => { alive = false; };
  }, [range, page]);

  if (!data) return <div className="py-12 text-center text-gray-400">Loading visitors…</div>;

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b flex items-center justify-between">
        <h3 className="text-sm font-bold text-gray-700">All visitors</h3>
        <span className="text-xs text-gray-400">{num(data.pagination.total)} total · click a row for full detail</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>
              <th className="text-left px-5 py-2 font-medium">Visitor</th>
              <th className="text-left px-4 py-2 font-medium">Device / browser</th>
              <th className="text-left px-4 py-2 font-medium">Location</th>
              <th className="text-left px-4 py-2 font-medium">Last page</th>
              <th className="text-right px-4 py-2 font-medium">Sessions</th>
              <th className="text-right px-4 py-2 font-medium">Views</th>
              <th className="text-right px-4 py-2 font-medium">Time</th>
              <th className="text-right px-5 py-2 font-medium">Last seen</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.visitors.map(v => (
              <tr key={v.visitorId} className="hover:bg-tea-50 cursor-pointer" onClick={() => setSelected(v.visitorId)}>
                <td className="px-5 py-2.5">
                  {v.user ? (
                    <>
                      <div className="font-medium text-gray-800">{v.user.name || v.user.email}</div>
                      <div className="text-xs text-gray-400">{v.user.email}</div>
                    </>
                  ) : (
                    <span className="text-gray-400 font-mono text-xs">{v.visitorId.slice(0, 8)}…</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-gray-600">{DEVICE_ICON[v.device] || ''} {v.browser} · {v.os}</td>
                <td className="px-4 py-2.5 text-gray-600">{v.region ? v.region + ', ' : ''}{v.country || '—'}</td>
                <td className="px-4 py-2.5 text-gray-600" title={v.lastPath}>{shortPath(v.lastPath)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{v.sessions}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{v.pageviews}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{dur(v.totalDurationMs)}</td>
                <td className="px-5 py-2.5 text-right text-xs text-gray-500">{formatDate(v.lastSeen)}</td>
              </tr>
            ))}
            {data.visitors.length === 0 && (
              <tr><td colSpan={8} className="px-5 py-8 text-center text-gray-400">No visitors in this range yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {data.pagination.pages > 1 && (
        <div className="flex items-center justify-between px-5 py-3 border-t text-sm">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-50">Previous</button>
          <span className="text-gray-500">Page {page} of {data.pagination.pages}</span>
          <button disabled={page >= data.pagination.pages} onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 rounded border disabled:opacity-40 hover:bg-gray-50">Next</button>
        </div>
      )}

      {selected && <VisitorModal visitorId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  FUNNEL — stage-by-stage conversion & drop-off
// ═══════════════════════════════════════════════════════════════════════════
function Funnel({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/admin/analytics/funnel', { params: { range } })
      .then(r => { if (alive) { setData(r.data); setLoading(false); } })
      .catch(() => { if (alive) { setLoading(false); toast.error('Failed to load funnel'); } });
    return () => { alive = false; };
  }, [range]);

  if (loading && !data) return <div className="py-12 text-center text-gray-400">Loading funnel…</div>;
  if (!data) return null;

  const top = data.steps[0]?.count || 0;

  return (
    <div className="space-y-6">
      {/* Headline conversion */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card label="Sessions entered" value={num(data.totalSessions)} accent />
        <Card label="Completed an order" value={num(data.steps[data.steps.length - 1]?.count || 0)} />
        <Card label="Overall conversion" value={`${data.overallConversion}%`} sub="visit → order" />
      </div>

      {/* Funnel steps */}
      <div className="bg-white rounded-xl border p-5 shadow-sm">
        <h3 className="text-sm font-bold text-gray-700 mb-1">Conversion funnel</h3>
        <p className="text-xs text-gray-400 mb-5">
          Each session is walked through the shopping steps in order — a session only counts at a
          step if it reached that page after the previous one. Percentages are share of everyone who entered.
        </p>

        {top === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center">No sessions in this range yet</div>
        ) : (
          <div className="space-y-1">
            {data.steps.map((s, i) => {
              const widthPct = top ? Math.max(3, (s.count / top) * 100) : 0;
              return (
                <div key={s.key}>
                  {/* Drop-off connector between steps */}
                  {i > 0 && (
                    <div className="flex items-center justify-center py-1">
                      {s.dropCount > 0 ? (
                        <span className="text-[11px] text-rose-500 font-medium tabular-nums">
                          ↓ {num(s.dropCount)} dropped off ({(100 - s.pctOfPrev).toFixed(1)}%)
                        </span>
                      ) : (
                        <span className="text-[11px] text-gray-300">↓</span>
                      )}
                    </div>
                  )}

                  <div className="relative rounded-lg border border-gray-100 bg-gray-50 overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-tea-500/15 border-r-2 border-tea-500"
                      style={{ width: `${widthPct}%` }}
                    />
                    <div className="relative flex items-center justify-between px-4 py-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-tea-600 text-white text-xs font-bold flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="font-medium text-gray-800 truncate">{s.label}</span>
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5 ml-8 truncate">{s.description}</div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <div className="text-lg font-bold tabular-nums text-gray-900">{num(s.count)}</div>
                        <div className="text-xs text-gray-500 tabular-nums">
                          {s.pctOfTop}% of visits
                          {i > 0 && <span className="text-gray-400"> · {s.pctOfPrev}% from prev</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  Page shell
// ═══════════════════════════════════════════════════════════════════════════
export default function AnalyticsPage() {
  const [range, setRange] = useState('24h');
  const [tab, setTab] = useState('overview');

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-2"><Icon name="analytics" className="h-7 w-7 text-tea-600" /> Analytics</h1>
        <div className="flex flex-wrap gap-1 bg-gray-100 rounded-lg p-1">
          {RANGES.map(r => (
            <button key={r.id} onClick={() => setRange(r.id)}
              className={'px-3 py-1.5 rounded-md text-sm font-medium transition-colors ' +
                (range === r.id ? 'bg-white text-tea-700 shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Live is always on top */}
      <LivePanel />

      {/* Sub-tabs */}
      <div className="flex gap-1 border-b mb-6">
        {[['overview', 'Overview'], ['funnel', 'Funnel'], ['visitors', 'Visitors']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ' +
              (tab === id ? 'border-tea-600 text-tea-700' : 'border-transparent text-gray-500 hover:text-gray-700')}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' ? <Overview range={range} />
        : tab === 'funnel' ? <Funnel range={range} />
        : <Visitors range={range} />}
    </div>
  );
}
